import {
  tableReservationsCancelationSucceed,
  tableReservationsClickOnReservationsAttribute,
  tableReservationCreateReservationSucceed,
  tableReservationsUouStartOfReservationFunnelClickOnFindATable,
  tableReservationsUouClickOnCompleteReservation,
  tableReservationsUouClickOnReserveNow,
  tableReservationsUouStartOfReservationFunnelReservationPageWidgetIsLoaded,
  tableReservationsUouClickOnChangeReservationDetails,
  tableReservationsUouClickOnAddToCalendar,
  tableReservationsClicks,
  tableReservationsBoReservationsModalOpened,
  restaurantsModalClose,
} from '@wix/bi-logger-restaurants/v2'
import {tableReservationEditorDisplayTabSrc159Evid1642} from '@wix/bi-logger-restaurants-data/v2'
import {
  tableReservationCreateReservationSucceedParams,
  tableReservationsCancelationSucceedParams,
} from '@wix/bi-logger-restaurants/v2/types'
import {
  Reservation,
  Source,
  Reservee,
} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {
  ReservationLocation,
  CustomFieldDefinition,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {VisitorLogger} from '@wix/yoshi-flow-editor/external-types/bi'

import {AppPage} from './constants'
import {
  configuredProtectionMethod,
  EffectiveProtectionMethod,
  getGoverningCancellationPolicy,
  hasPrepayment,
  isProtectionActive,
} from './cancellationPolicy'

enum ViewMode {
  preview = 'preview_mode',
  liveSite = 'live_site_mode',
}

export enum PageType {
  page = 'page',
  widget = 'widget',
}

export enum Checkbox {
  check = 'check',
  uncheck = 'uncheck',
}

export enum FieldName {
  location = 'location',
  partySize = 'party_size',
  date = 'date',
  time = 'time',
  timeSlot = 'time_slot',
  area = 'area',
}

export enum BiReservationType {
  reservation = 'reservation',
  walkIn = 'walk_in',
}

export enum BiPageName {
  experiences = 'experiences',
  experienceDetails = 'experience_details',
  reservations = 'reservations',
  reservationDetails = 'reservation_details',
  reservationConfirmation = 'reservation_confirmation',
}

export const AppPageToBiPageName: Record<AppPage, BiPageName> = {
  [AppPage.ExperiencesList]: BiPageName.experiences,
  [AppPage.ExperienceDetails]: BiPageName.experienceDetails,
  [AppPage.Reservations]: BiPageName.reservations,
  [AppPage.ReservationDetails]: BiPageName.reservationDetails,
  [AppPage.ReservationConfirmation]: BiPageName.reservationConfirmation,
} as const

interface ReservationCreatedParams {
  isPreview: boolean
  reservation: Reservation
  reservationLocation?: ReservationLocation
  reservee: Reservee
  isCardTokenization: boolean
  referer?: string
}

type ReservationFunnelIsLoadedParams = {
  areaSelectionDisplayed?: boolean
  numAreas?: number
  formLayout?: string
  cancellationPolicyDisclosureShown?: boolean
}

export const getLogger = (logger: Pick<VisitorLogger, 'report'>) => {
  const isLoaded = ({
    isAddOn,
    isPreview,
    pageType,
    pageName,
    experienceId,
    areaSelectionDisplayed,
    numAreas,
    formLayout,
    cancellationPolicyDisclosureShown,
  }: {
    isAddOn: boolean
    isPreview: boolean
    pageType: PageType
    pageName?: AppPage
    experienceId?: string
  } & ReservationFunnelIsLoadedParams) => {
    logger.report(
      tableReservationsUouStartOfReservationFunnelReservationPageWidgetIsLoaded({
        isAddOn,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        type: pageType,
        pageName: pageName ? AppPageToBiPageName[pageName] : undefined,
        experienceId,
        areaSelectionDisplayed: areaSelectionDisplayed?.toString(),
        numAreas: numAreas?.toString(),
        ...{
          formLayout,
          cancellationPolicyDisclosureShown: cancellationPolicyDisclosureShown?.toString(),
        },
      }),
    )
  }

  const clickOnReserveNow = ({
    locationId,
    reservationLocationId,
    isPreview,
    partySize,
    requestedDate,
  }: {
    locationId?: string
    reservationLocationId: string
    isPreview: boolean
    partySize: number
    requestedDate: Date
  }) => {
    logger.report(
      tableReservationsUouClickOnReserveNow({
        locationId,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        partySize,
        requestedDate: requestedDate.getTime(),
        reservationLocationId,
      }),
    )
  }

  const clickOnCompleteReservation = (
    {
      locationId,
      mailCheckbox,
      numInvaldFields,
      numValidFields,
      areaCode,
      isPreview,
      reservationLocationId,
      cancellationFeeApplicable,
    }: {
      locationId?: string | null
      reservationLocationId?: string | null
      mailCheckbox: boolean
      numInvaldFields: number
      numValidFields: number
      areaCode: string
      isPreview: boolean
      cancellationFeeApplicable: boolean
    },
    reservation?: Reservation,
  ) => {
    logger.report(
      tableReservationsUouClickOnCompleteReservation({
        areaCode,
        contactId: reservation?.reservee?.contactId ?? undefined,
        dateTimeValue: reservation?.details?.startDate?.getTime(),
        locationId: locationId ?? undefined,
        mailCheckbox: mailCheckbox?.toString() ?? undefined,
        numInvaldFields,
        numValidFields,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        partySize: reservation?.details?.partySize ?? undefined,
        reservationLocationId: reservationLocationId ?? undefined,
        cancellationFeeApplicable: cancellationFeeApplicable.toString(),
      }),
    )
  }

  const clickOnFindATable = ({
    datetime,
    isMultiLocation,
    locationId,
    isPreview,
    partySize,
    reservationLocationId,
  }: {
    datetime: Date
    isMultiLocation: boolean
    locationId?: string | null
    reservationLocationId: string
    isPreview: boolean
    partySize: number
  }) => {
    logger.report(
      tableReservationsUouStartOfReservationFunnelClickOnFindATable({
        datetime: datetime.getTime(),
        isAddOn: true,
        isMultiLocation,
        locationId: locationId ?? undefined,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        partySize,
        reservationLocationId,
      }),
    )
  }

  const reservationCreated = (params: ReservationCreatedParams) => {
    logger.report(
      tableReservationCreateReservationSucceed({
        saveType: 'create',
        ...getParamsNew(params),
      }),
    )
  }

  const reservationCanceled = ({
    reservation,
    isPreview,
    locationId,
    cancellationFeeApplicable,
  }: {
    reservation: Reservation
    isPreview: boolean
    locationId?: string | null
    cancellationFeeApplicable?: boolean
  }) => {
    const params: tableReservationsCancelationSucceedParams = {
      datetime: reservation.details?.startDate?.getTime() ?? undefined,
      locationId: locationId ?? undefined,
      origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
      partySize: reservation.details?.partySize ?? undefined,
      reservationId: reservation.id ?? undefined,
      reservationLocationId: reservation.details?.reservationLocationId ?? undefined,
      reservationStatus: reservation.status,
      cancellationFeeApplicable: cancellationFeeApplicable?.toString(),
    }

    logger.report(tableReservationsCancelationSucceed(params))
  }

  const cancelReservationIntent = ({
    reservation,
    locationId,
    phoneValid,
    cancellationFeeApplicable,
    cancellationFeeShown,
  }: {
    reservation: Reservation
    locationId?: string | null
    phoneValid: boolean
    cancellationFeeApplicable?: boolean
    cancellationFeeShown?: boolean
  }) => {
    logger.report(
      tableReservationsClicks({
        pageName: BiPageName.reservationConfirmation,
        actionName: 'confirm_cancel_reservation',
        placement: 'ooi_cancel_modal',
        selection: phoneValid ? 'phone_valid' : 'phone_invalid',
        reservation__id: reservation.id ?? undefined,
        reservationLocationId: reservation.details?.reservationLocationId ?? undefined,
        locationId: locationId ?? undefined,
        additionalData: JSON.stringify({
          cancellation_fee_applicable: cancellationFeeApplicable,
          cancellation_fee_shown: cancellationFeeShown,
        }),
      }),
    )
  }

  const cancelReservationModalOpened = ({
    reservation,
    locationId,
    cancellationFeeShown,
  }: {
    reservation: Reservation
    locationId?: string | null
    cancellationFeeShown: boolean
  }) => {
    logger.report(
      tableReservationsBoReservationsModalOpened({
        modalType: 'ooi_cancel_modal',
        reservationId: reservation.id ?? undefined,
        reservationLocationId: reservation.details?.reservationLocationId ?? undefined,
        locationId: locationId ?? undefined,
        reservationType: BiReservationType.reservation,
        type: 'edit',
        cancellationFeeBannerShown: cancellationFeeShown.toString(),
      }),
    )
  }

  const cancelReservationDismissed = ({
    origin,
    cancellationFeeShown,
  }: {
    origin: string
    cancellationFeeShown: boolean
  }) => {
    logger.report(
      restaurantsModalClose({
        product: 'table_reservations',
        pageName: BiPageName.reservationConfirmation,
        modalName: cancellationFeeShown
          ? 'table_reservations.cancellation-fee'
          : 'table_reservations.cancel-reservation',
        origin,
      }),
    )
  }

  const clickOnReservationsAttributes = ({
    fieldName,
    value,
    isAddOn,
    isMultiLocation,
    isPreview,
    locationId,
    reservationLocationId,
    experienceId,
  }: {
    fieldName: FieldName
    value: string
    isAddOn: boolean
    isMultiLocation: boolean
    isPreview: boolean
    locationId?: string | null
    reservationLocationId?: string | null
    experienceId?: string | null
  }) => {
    logger.report(
      tableReservationsClickOnReservationsAttribute({
        fieldName,
        value,
        isAddOn,
        isMultiLocation,
        locationId: locationId ?? undefined,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        reservationLocationId: reservationLocationId ?? undefined,
        experienceId: experienceId ?? undefined,
      }),
    )
  }

  const changedSetting = ({
    action,
    value,
    isAddon = false,
    widgetName,
    builderType,
    tabName,
  }: {
    action: string
    value: string
    isAddon?: boolean
    widgetName?: string
    builderType?: string
    tabName?: string
  }) =>
    logger.report(
      tableReservationEditorDisplayTabSrc159Evid1642({
        action,
        value,
        is_add_on: isAddon,
        widgetName,
        builderType,
        tabName,
      }),
    )

  const clickOnChangeReservationDetails = ({
    locationId,
    reservation,
    reservationLocationId,
    isPreview,
  }: {
    locationId?: string | null
    reservation: Reservation
    reservationLocationId?: string | null
    isPreview: boolean
  }) => {
    logger.report(
      tableReservationsUouClickOnChangeReservationDetails({
        locationId: locationId ?? undefined,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        reservationId: reservation.id ?? undefined,
        reservationLocationId: reservationLocationId ?? undefined,
        reservationStatus: reservation.status === 'HELD' ? reservation.status : undefined,
      }),
    )
  }

  const clickOnAddToCalendar = ({
    locationId,
    reservation,
    reservationLocationId,
    isPreview,
    isSuccessful,
  }: {
    locationId?: string | null
    reservation: Reservation
    reservationLocationId?: string | null
    isPreview: boolean
    isSuccessful: boolean
  }) => {
    logger.report(
      tableReservationsUouClickOnAddToCalendar({
        locationId: locationId ?? undefined,
        origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
        reservationId: reservation.id ?? undefined,
        reservationLocationId: reservationLocationId ?? undefined,
        isSuccessful,
      }),
    )
  }

  return {
    isLoaded,
    clickOnReserveNow,
    clickOnCompleteReservation,
    clickOnFindATable,
    reservationCreated,
    reservationCanceled,
    cancelReservationIntent,
    cancelReservationModalOpened,
    cancelReservationDismissed,
    clickOnReservationsAttributes,
    clickOnChangeReservationDetails,
    changedSetting,
    clickOnAddToCalendar,
  }
}

const getParamsNew = ({
  isPreview,
  reservation,
  reservationLocation,
  reservee,
  isCardTokenization,
  referer,
}: ReservationCreatedParams): tableReservationCreateReservationSucceedParams & {
  paymentProtectionType: EffectiveProtectionMethod
} => {
  const createdDate = reservation?.createdDate
  const startDate = reservation?.details?.startDate
  const endDate = reservation?.details?.endDate

  const additionalDetails = reservation?.reservee?.customFields
    ? JSON.stringify(reservation.reservee.customFields)
    : undefined

  const reservationDuration =
    endDate && startDate ? endDate.getTime() - startDate.getTime() : undefined

  const reservationType = reservation?.source
    ? reservation.source === Source.WALK_IN
      ? BiReservationType.walkIn
      : BiReservationType.reservation
    : undefined
  const cancellationPolicy =
    reservation.cancellationPolicy ??
    getGoverningCancellationPolicy(reservationLocation, reservation.details?.experienceId)

  return {
    additionalDetails,
    contactId: reservation?.reservedBy?.contactId ?? undefined,
    creationDate: createdDate?.getTime(),
    mailChecked: reservee?.marketingConsent ?? undefined,
    ...getNumAdditionalDetails(
      reservationLocation?.configuration?.reservationForm?.customFieldDefinitions ?? [],
      reservee?.customFields,
    ),
    ...getNumGuestDetail(reservee),
    origin: isPreview ? ViewMode.preview : ViewMode.liveSite,
    partySize: reservation?.details?.partySize ?? undefined,
    reservationId: reservation?.id ?? undefined,
    reservationDuration,
    statusCurrent: reservation.status,
    reservationLocationId: reservation?.details?.reservationLocationId ?? undefined,
    reservationStartDate: reservation?.details?.startDate?.getTime() ?? undefined,
    reservationType,
    paymentStatus: reservation?.paymentStatus,
    prepaymentType: reservationLocation?.configuration?.paymentPolicy?.paymentPolicyType,
    paymentProtectionType: configuredProtectionMethod(
      cancellationPolicy,
      hasPrepayment(reservationLocation),
    ),
    isCardTokenization,
    referer,
    experienceId: reservation?.details?.experienceId ?? undefined,
    areaId: reservation?.details?.areaId ?? undefined,
    cancellationFeeApplicable: isProtectionActive(
      cancellationPolicy,
      reservation.details?.partySize,
    ).toString(),
  }
}

const getNumAdditionalDetails = (
  customFieldDefinitions: CustomFieldDefinition[],
  customFields: Reservee['customFields'],
) => {
  let numAdditionalDetailsEmpty: number | undefined
  let numAdditionalDetailsFull: number | undefined
  let numAdditionalDetailsMandatory: number | undefined

  customFieldDefinitions.forEach(({id, required}) => {
    const customFieldValue = id && customFields?.[id] ? customFields[id] : undefined

    if (customFieldValue) {
      numAdditionalDetailsFull = numAdditionalDetailsFull ? numAdditionalDetailsFull + 1 : 1
    } else {
      numAdditionalDetailsEmpty = numAdditionalDetailsEmpty ? numAdditionalDetailsEmpty + 1 : 1
    }

    if (required) {
      numAdditionalDetailsMandatory = numAdditionalDetailsMandatory
        ? numAdditionalDetailsMandatory++
        : 1
    }
  })

  return {
    numAdditionalDetailsEmpty,
    numAdditionalDetailsFull,
    numAdditionalDetailsMandatory,
  }
}

const getNumGuestDetail = (reservee?: Reservee) => {
  let numGuestDetailEmpty: number | undefined
  let numGuestDetailFull: number | undefined
  const guestDetails = [reservee?.firstName, reservee?.lastName, reservee?.phone, reservee?.email]

  guestDetails.forEach((val) => {
    if (val) {
      numGuestDetailFull = numGuestDetailFull ? numGuestDetailFull + 1 : 1
    } else {
      numGuestDetailEmpty = numGuestDetailEmpty ? numGuestDetailEmpty + 1 : 1
    }
  })

  return {
    numGuestDetailEmpty,
    numGuestDetailFull,
  }
}
